import {
  DefaultChatTransport,
  isTextUIPart,
  readUIMessageStream,
  type UIMessage,
} from "ai";
import { useCallback, useRef } from "react";

import { authFetch } from "@phoenix/authFetch";
import { useAgentStore } from "@phoenix/contexts/AgentContext";

import {
  getFirstAssistantMessageText,
  getFirstUserMessageText,
} from "./sessionSummaryUtils";

const SUMMARY_SYSTEM_PROMPT =
  "You are a concise summarizer. Respond with ONLY a 5-10 word summary of the conversation topic for presentation to the user. No quotes, no punctuation at the end, no preamble.";

/**
 * Sends a lightweight summarization request through a {@link DefaultChatTransport}
 * and reads the full response text using {@link readUIMessageStream}.
 *
 * This reuses the same stream parsing that `useChat` relies on internally,
 * so we don't need to hand-roll any protocol-specific logic.
 */
async function fetchSummary({
  chatApiUrl,
  userMessageText,
  assistantMessageText,
}: {
  chatApiUrl: string;
  userMessageText: string;
  assistantMessageText: string;
}): Promise<string> {
  const transport = new DefaultChatTransport({
    api: chatApiUrl,
    fetch: authFetch,
  });

  const summarizePrompt = [
    "Summarize this conversation in 5-10 words:",
    `User: ${userMessageText}`,
    `Assistant: ${assistantMessageText}`,
  ].join("\n");

  const chunkStream = await transport.sendMessages({
    trigger: "submit-message",
    chatId: crypto.randomUUID(),
    messageId: undefined,
    messages: [
      {
        id: crypto.randomUUID(),
        role: "system" as const,
        parts: [{ type: "text" as const, text: SUMMARY_SYSTEM_PROMPT }],
      },
      {
        id: crypto.randomUUID(),
        role: "user" as const,
        parts: [{ type: "text" as const, text: summarizePrompt }],
      },
    ],
    abortSignal: undefined,
  });

  // Iterate the stream to completion; each yielded value is the progressively
  // updated assistant message. We only care about the final state.
  let finalMessage: UIMessage | undefined;
  for await (const message of readUIMessageStream({ stream: chunkStream })) {
    finalMessage = message;
  }

  if (!finalMessage) return "";

  return finalMessage.parts
    .filter(isTextUIPart)
    .map((part) => part.text)
    .join("")
    .trim();
}

/**
 * Hook that provides a function to asynchronously generate a session summary
 * after the first assistant response completes.
 *
 * The summary is generated by making a separate, lightweight call to the
 * same `/chat` endpoint using the currently selected model. Stream parsing
 * is delegated to the AI SDK's {@link DefaultChatTransport} and
 * {@link readUIMessageStream} — the same pipeline that powers `useChat` —
 * so we don't maintain any custom protocol parsing.
 *
 * @param params.chatApiUrl - the chat endpoint URL (includes model params)
 */
export function useGenerateSessionSummary({
  chatApiUrl,
}: {
  chatApiUrl: string;
}) {
  const store = useAgentStore();
  // Track which sessions have already had summaries requested to avoid
  // duplicate fire-and-forget calls.
  const requestedSessionsRef = useRef(new Set<string>());

  const generateSummary = useCallback(
    ({ sessionId }: { sessionId: string }) => {
      // Only generate once per session
      if (requestedSessionsRef.current.has(sessionId)) return;

      const state = store.getState();
      const session = state.sessionMap[sessionId];
      if (!session) return;

      // Only generate if there's no summary yet
      if (session.shortSummary) return;

      const userText = getFirstUserMessageText(session.messages);
      const assistantText = getFirstAssistantMessageText(session.messages);
      if (!userText || !assistantText) return;

      requestedSessionsRef.current.add(sessionId);

      // Fire-and-forget: don't block the UI
      void fetchSummary({
        chatApiUrl,
        userMessageText: userText,
        assistantMessageText: assistantText,
      })
        .then((summary) => {
          if (summary) {
            store.getState().updateSessionSummary(sessionId, summary);
          }
        })
        .catch(() => {
          // Summary generation is best-effort; silently swallow errors.
          // Remove from the set so a retry can happen if the user triggers
          // another exchange.
          requestedSessionsRef.current.delete(sessionId);
        });
    },
    [chatApiUrl, store]
  );

  return { generateSummary };
}
